import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

export const useRelances = (options = {}) => {
  const { autoLoad = true, filters = {} } = options;
  const filtersRef = useRef(filters);

  const [relances, setRelances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadRelances = useCallback(async (customFilters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = { ...filtersRef.current, ...customFilters };
      const response = await api.relances.getAll(params);
      setRelances(response.relances || []);
      return response.relances;
    } catch (err) {
      const message = err.message || 'Erreur lors du chargement des relances';
      setError(message);
      console.error('Erreur loadRelances:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const createRelance = useCallback(async (data) => {
    setError(null);
    try {
      const response = await api.relances.create(data);
      const newRelance = response.relance;
      setRelances(prev => [newRelance, ...prev]);
      return { success: true, relance: newRelance };
    } catch (err) {
      const message = err.message || 'Erreur lors de la création';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const updateRelance = useCallback(async (id, data) => {
    setError(null);
    try {
      const response = await api.relances.update(id, data);
      const updated = response.relance;
      setRelances(prev => prev.map(r => r.id === id ? updated : r));
      return { success: true, relance: updated };
    } catch (err) {
      const message = err.message || 'Erreur lors de la mise à jour';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const deleteRelance = useCallback(async (id) => {
    setError(null);
    try {
      await api.relances.delete(id);
      setRelances(prev => prev.filter(r => r.id !== id));
      return { success: true };
    } catch (err) {
      const message = err.message || 'Erreur lors de la suppression';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const marquerEffectue = useCallback(async (id, data = {}) => {
    setError(null);
    try {
      const response = await api.relances.marquerEffectue(id, data);
      const updated = response.relance;
      setRelances(prev => prev.map(r => r.id === id ? updated : r));
      return { success: true, relance: updated };
    } catch (err) {
      const message = err.message || 'Erreur';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const reporter = useCallback(async (id, data) => {
    setError(null);
    try {
      const response = await api.relances.reporter(id, data);
      const updated = response.relance;
      setRelances(prev => prev.map(r => r.id === id ? updated : r));
      return { success: true, relance: updated };
    } catch (err) {
      const message = err.message || 'Erreur';
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  useEffect(() => {
    if (autoLoad) {
      loadRelances();
    }
  }, [autoLoad, loadRelances]);

  return {
    relances,
    loading,
    error,
    loadRelances,
    createRelance,
    updateRelance,
    deleteRelance,
    marquerEffectue,
    reporter,
  };
};

export default useRelances;
